/**
 * Servicio para manejar la API de configuración del sistema
 * Proporciona funciones para gestionar configuraciones globales y por sucursal
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '@/config/api';

/**
 * Obtiene toda la configuración del sistema
 * @returns {Promise<Array>} - Lista de configuraciones
 */
export const getConfiguracion = async () => {
  try {
    //console.log('⚙️ [ConfiguracionService] Obteniendo configuración...');
    
    let endpoint = API_ENDPOINTS.CONFIGURACIONES.BASE;
    const params = {};

    const response = await apiClient.get(endpoint, { params });
    
    if (response.data) {
      const configuracionData = response.data.data || response.data.configuracion || response.data;
      //console.log('✅ [ConfiguracionService] Configuración obtenida:', configuracionData.length);
      return configuracionData;
    } else {
      console.warn('⚠️ [ConfiguracionService] No se encontró configuración');
      return [];
    }
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al obtener configuración:', error);
    throw error;
  }
};

/**
 * Obtiene un valor de configuración específico por clave
 * @param {string} clave - Clave de configuración
 * @returns {Promise<any>} - Valor de la configuración
 */
export const getConfiguracionPorClave = async (clave) => {
  try {
    console.log('⚙️ [ConfiguracionService] Obteniendo configuración por clave:', { clave });
    
    // Usar el endpoint base con query parameter clave
    const endpoint = API_ENDPOINTS.CONFIGURACIONES.BASE;
    const params = { clave };
    
    const response = await apiClient.get(endpoint, { params });
    
    console.log('⚙️ [ConfiguracionService] Respuesta:', response.data);
    
    if (response.data) {
      const configuracionData = response.data.data || response.data.configuracion || response.data;
      console.log('✅ [ConfiguracionService] Configuración por clave obtenida:', configuracionData);
      
      // Si es un array, buscar la configuración con la clave específica
      if (Array.isArray(configuracionData) && configuracionData.length > 0) {
        const config = configuracionData.find(c => 
          (c.clave === clave || c.Clave === clave)
        );
        if (config) {
          return config.valor || config.Valor;
        } else {
          console.warn('⚠️ [ConfiguracionService] No se encontró configuración con clave:', clave);
          return null;
        }
      }
      
      // Si es un objeto directamente
      if (configuracionData.valor !== undefined || configuracionData.Valor !== undefined) {
        return configuracionData.valor || configuracionData.Valor;
      }
      
      console.warn('⚠️ [ConfiguracionService] Formato de configuración inesperado:', configuracionData);
      return null;
    } else {
      console.warn('⚠️ [ConfiguracionService] Configuración no encontrada para clave:', clave);
      return null;
    }
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al obtener configuración por clave:', error);
    throw error;
  }
};

/**
 * Crea o actualiza una configuración
 * @param {Object} configuracionData - Datos de la configuración
 * @returns {Promise<Object>} - Configuración creada/actualizada
 */
export const setConfiguracion = async (configuracionData) => {
  try {
    //console.log('⚙️ [ConfiguracionService] Guardando configuración:', configuracionData);
    
    const response = await apiClient.post(API_ENDPOINTS.CONFIGURACIONES.BASE, configuracionData);
    
    if (response.data) {
      const configuracion = response.data.data || response.data.configuracion || response.data;
      //console.log('✅ [ConfiguracionService] Configuración guardada:', configuracion);
      return configuracion;
    } else {
      throw new Error('Error al guardar la configuración');
    }
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al guardar configuración:', error);
    throw error;
  }
};

/**
 * Actualiza una configuración existente
 * @param {string} clave - Clave de configuración
 * @param {Object} configuracionData - Datos actualizados
 * @returns {Promise<Object>} - Configuración actualizada
 */
export const updateConfiguracion = async (clave, configuracionData) => {
  try {
    //console.log('⚙️ [ConfiguracionService] Actualizando configuración:', { clave, configuracionData });
    
    const endpoint = API_ENDPOINTS.CONFIGURACIONES.BY_CLAVE(clave);
    const params = {};
    
    const response = await apiClient.put(endpoint, configuracionData, { params });
    
    if (response.data) {
      const configuracion = response.data.data || response.data.configuracion || response.data;
      //console.log('✅ [ConfiguracionService] Configuración actualizada:', configuracion);
      return configuracion;
    } else {
      throw new Error('Error al actualizar la configuración');
    }
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al actualizar configuración:', error);
    throw error;
  }
};

/**
 * Elimina una configuración
 * @param {string} clave - Clave de configuración
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export const deleteConfiguracion = async (clave) => {
  try {
    //console.log('⚙️ [ConfiguracionService] Eliminando configuración:', { clave });
    
    const endpoint = API_ENDPOINTS.CONFIGURACIONES.BY_CLAVE(clave);
    const params = {};
    
    const response = await apiClient.delete(endpoint, { params });
    
    //console.log('✅ [ConfiguracionService] Configuración eliminada correctamente');
    return true;
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al eliminar configuración:', error);
    throw error;
  }
};

/**
 * Obtiene configuraciones editables por el taller
 * @returns {Promise<Array>} - Lista de configuraciones editables
 */
export const getConfiguracionEditablePorTaller = async () => {
  try {
    //console.log('⚙️ [ConfiguracionService] Obteniendo configuración editable por taller');
    
    const configuraciones = await getConfiguracion();
    
    // Filtrar solo las editables por taller
    const configuracionesEditables = configuraciones.filter(config => 
      config.editable_por_taller === true || config.EditablePorTaller === true
    );
    
    //console.log('✅ [ConfiguracionService] Configuraciones editables obtenidas:', configuracionesEditables.length);
    return configuracionesEditables;
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al obtener configuración editable:', error);
    throw error;
  }
};

/**
 * Guarda múltiples configuraciones en lote
 * @param {Array} configuraciones - Array de configuraciones a guardar
 * @returns {Promise<Array>} - Configuraciones guardadas
 */
export const saveConfiguraciones = async (configuraciones) => {
  try {
    //console.log('⚙️ [ConfiguracionService] Guardando configuraciones en lote:', configuraciones.length);
    
    const configuracionesGuardadas = [];
    
    for (const config of configuraciones) {
      try {
        const configuracionGuardada = await setConfiguracion(config);
        configuracionesGuardadas.push(configuracionGuardada);
      } catch (error) {
        console.error(`Error al guardar configuración ${config.clave}:`, error);
        // Continuar con las demás configuraciones
      }
    }
    
    //console.log('✅ [ConfiguracionService] Configuraciones guardadas en lote:', configuracionesGuardadas.length);
    return configuracionesGuardadas;
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al guardar configuraciones en lote:', error);
    throw error;
  }
};

/**
 * Sube un logo y lo guarda en la configuración
 * @param {File} file - Archivo de imagen
 * @param {number} empresaId - ID de la empresa
 * @param {string} tipo - Tipo de logo
 * @returns {Promise<string>} - URL o base64 del logo
 */
export const uploadLogo = async (file, empresaId = null, tipo = 'logo_empresa') => {
  try {
    //console.log('⚙️ [ConfiguracionService] Subiendo logo:', { tipo, empresaId });
    
    // Validar que sea un archivo de imagen
    if (!file.type.startsWith('image/')) {
      throw new Error('El archivo debe ser una imagen');
    }
    
    // Validar tamaño (máximo 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      throw new Error('El archivo no debe superar los 2MB');
    }
    
    // Convertir archivo a base64
    const base64String = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsDataURL(file);
    });
    
    // Guardar base64 en configuración
    const configuracionData = {
      clave: `${tipo}_base64`,
      valor: base64String,
      tipo: 'string',
      descripcion: `${tipo} en base64`,
      empresa_id: empresaId,
      editable_por_taller: false
    };
    
    await setConfiguracion(configuracionData);
    
    //console.log('✅ [ConfiguracionService] Logo subido correctamente');
    return base64String;
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al subir logo:', error);
    throw error;
  }
};

/**
 * Exporta toda la configuración para respaldo
 * @returns {Promise<Object>} - Configuración exportada
 */
export const exportarConfiguracion = async () => {
  try {
    //console.log('⚙️ [ConfiguracionService] Exportando configuración');
    
    const configuraciones = await getConfiguracion();
    
    const configExportada = {
      fecha_exportacion: new Date().toISOString(),
      configuraciones: configuraciones.reduce((acc, config) => {
        acc[config.clave || config.Clave] = {
          valor: config.valor || config.Valor,
          tipo: config.tipo || config.Tipo,
          descripcion: config.descripcion || config.Descripcion
        };
        return acc;
      }, {})
    };
    
    //console.log('✅ [ConfiguracionService] Configuración exportada');
    return configExportada;
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al exportar configuración:', error);
    throw error;
  }
};

/**
 * Importa configuración desde un respaldo
 * @param {Object} configData - Datos de configuración a importar
 * @returns {Promise<Array>} - Configuraciones importadas
 */
export const importarConfiguracion = async (configData) => {
  try {
    //console.log('⚙️ [ConfiguracionService] Importando configuración');
    
    const configuracionesAImportar = Object.entries(configData.configuraciones).map(([clave, datos]) => ({
      clave,
      valor: datos.valor,
      tipo: datos.tipo,
      descripcion: datos.descripcion,
      editable_por_taller: true
    }));
    
    const configuracionesImportadas = await saveConfiguraciones(configuracionesAImportar);
    
    //console.log('✅ [ConfiguracionService] Configuración importada:', configuracionesImportadas.length);
    return configuracionesImportadas;
  } catch (error) {
    console.error('❌ [ConfiguracionService] Error al importar configuración:', error);
    throw error;
  }
};

// Alias para compatibilidad
export const saveConfiguracion = setConfiguracion;